// オブジェクト配置時の既定サイズ（ステージ座標 px）。
//
// ツールバーからの配置（canvas-stage.tsx の onPlaceObject）が使う既定値の単一正本。
// このうちテキストの初期高さ（textInitialHeight）だけは、コネクタからのオブジェクト自動作成
// （connector-auto-create.ts）も参照する（ほかはサイズを接続元から継承するため参照しない）。
// 片方だけ変更されて挙動が食い違うのを防ぐため、定数だけでなく算出式ごとここへ集約する。
//
// 種類ごとの「最後に配置・リサイズしたサイズ」の記憶（lib/placement-size-preference.ts）が使う
// 記憶の単位・既定・下限・上限・縦横比の制約も、配置経路（クリック・ドラッグ・ダブルクリック・
// 右クリックメニュー）で食い違わないよう、ここを単一の正本にする。

import {
  ARROW_MIN_SIZE_PX,
  CANVAS_SIZE_PRESETS,
  SECTION_DEFAULT_H,
  SECTION_DEFAULT_W,
  SECTION_MIN_H,
  SECTION_MIN_W,
  type CanvasObject,
  type ShapeCanvasObject,
} from "@refinement-canvas/domain";
import type { PlaceableObjectKind } from "../../lib/last-placed-object-preference";
import { resolveShapeDefaultFrameSize } from "../../lib/shape-default-size";
import {
  STAMP_PLACEMENT_MAX_PX,
  STAMP_PLACEMENT_MIN_PX,
  resolveStampPlacementSize,
} from "../../lib/stamp-placement-size";

export const STICKY_DEFAULT_W = 200;
export const STICKY_DEFAULT_H = 160;
export const TEXT_DEFAULT_W = 240;
/** テキストの既定の高さ。文字サイズに応じた初期高さの下限として textInitialHeight が使う。 */
export const TEXT_DEFAULT_H = 48;

/**
 * 文字サイズから決まるテキストの初期高さ。極端に大きい文字でも最低 1 行分を確保する。
 * ツールバーからの配置とコネクタからの自動作成で結果が食い違わないよう、式ごとここを正本にする。
 */
export function textInitialHeight(fontSize: number): number {
  return Math.max(TEXT_DEFAULT_H, Math.ceil(fontSize * 1.6));
}

/** 表図形の既定サイズと初期セル数（ヘッダ 1 行 + 本文 2 行）。 */
export const TABLE_DEFAULT_W = 360;
export const TABLE_DEFAULT_H = 96;
export const TABLE_DEFAULT_ROWS = 3;
export const TABLE_DEFAULT_COLUMNS = 3;

/**
 * 通常の配置サイズの下限（ステージ px）。リサイズハンドルの最小サイズ（use-resize-handle.ts の
 * MIN_SIZE_PX）と、図形のドラッグ配置の最小サイズ（use-pointer-mode.ts の SHAPE_DRAG_MIN_SIZE_PX）も
 * この値を参照し、3 つの下限が食い違わないようにする。
 * 既定サイズを下限へ流用しないため、付箋・テキストもドラッグで既定より小さく置ける。
 */
export const PLACEMENT_MIN_SIZE_PX = 16;

/**
 * 配置サイズの上限（ステージ px）。domain はオブジェクトの寸法に上限を設けていないため、
 * 最大のキャンバスプリセット（12K）の幅・高さを上限とする。どのキャンバスにも収まらない
 * 大きさは、記憶値の破損（巨大値）として扱う。
 */
export const PLACEMENT_MAX_W = Math.max(
  ...Object.values(CANVAS_SIZE_PRESETS).map((preset) => preset.width)
);
export const PLACEMENT_MAX_H = Math.max(
  ...Object.values(CANVAS_SIZE_PRESETS).map((preset) => preset.height)
);

/**
 * 配置サイズを記憶する単位。図形だけは種類（四角・丸・矢印）ごとに分け、スタンプの絵柄や
 * 色・テキストの書式は同じ種類の中でサイズを共用する。
 */
export const PLACEMENT_SIZE_KEYS = [
  "sticky",
  "text",
  "section",
  "shape:rectangle",
  "shape:circle",
  "shape:arrow",
  "table",
  "stamp",
] as const;

export type PlacementSizeKey = (typeof PLACEMENT_SIZE_KEYS)[number];

type ShapeKind = ShapeCanvasObject["shapeKind"];

/** 配置する種別（図形は種類まで）からサイズ記憶の単位を求める。 */
export function placementSizeKeyOf(
  kind: PlaceableObjectKind,
  shapeKind: ShapeKind
): PlacementSizeKey {
  return kind === "shape" ? `shape:${shapeKind}` : kind;
}

/**
 * 既存オブジェクトのサイズ記憶の単位を求める。画像・動画・リンクカード・ペン・コネクタ・コメントは
 * 記憶の対象外（クリックや前回種別の再配置で置く種別ではない）のため null を返す。
 */
export function placementSizeKeyOfObject(object: CanvasObject): PlacementSizeKey | null {
  switch (object.type) {
    case "sticky":
    case "text":
    case "section":
    case "table":
    case "stamp":
      return object.type;
    case "shape":
      return `shape:${object.shapeKind}`;
    default:
      return null;
  }
}

/** 配置サイズの制約（ステージ px）。 */
export interface PlacementSizeLimits {
  readonly minW: number;
  readonly minH: number;
  readonly maxW: number;
  readonly maxH: number;
  /** 縦横比を 1:1 に固定する種類か（スタンプ）。 */
  readonly square: boolean;
}

/** 記憶の単位ごとの配置サイズの制約を返す。 */
export function placementSizeLimitsOf(key: PlacementSizeKey): PlacementSizeLimits {
  switch (key) {
    case "section":
      // タイトルバーが潰れない下限（リサイズハンドル・ドラッグ配置と同じ 200×120）。
      return {
        minW: SECTION_MIN_W,
        minH: SECTION_MIN_H,
        maxW: PLACEMENT_MAX_W,
        maxH: PLACEMENT_MAX_H,
        square: false,
      };
    case "table":
      // 空の 3×3 表が潰れないよう、ドラッグ配置と同じく既定サイズを下限にする。
      return {
        minW: TABLE_DEFAULT_W,
        minH: TABLE_DEFAULT_H,
        maxW: PLACEMENT_MAX_W,
        maxH: PLACEMENT_MAX_H,
        square: false,
      };
    case "shape:arrow":
      // クリック配置する軸そろえの矢印のリサイズ下限（resolveResizeMinSize）と同じ 8px。
      return {
        minW: ARROW_MIN_SIZE_PX,
        minH: ARROW_MIN_SIZE_PX,
        maxW: PLACEMENT_MAX_W,
        maxH: PLACEMENT_MAX_H,
        square: false,
      };
    case "stamp":
      // 拡大率から決める初期サイズと同じ安全弁（8〜512px）で抑え、縦横比は 1:1 に保つ。
      return {
        minW: STAMP_PLACEMENT_MIN_PX,
        minH: STAMP_PLACEMENT_MIN_PX,
        maxW: STAMP_PLACEMENT_MAX_PX,
        maxH: STAMP_PLACEMENT_MAX_PX,
        square: true,
      };
    case "sticky":
    case "text":
    case "shape:rectangle":
    case "shape:circle":
      return {
        minW: PLACEMENT_MIN_SIZE_PX,
        minH: PLACEMENT_MIN_SIZE_PX,
        maxW: PLACEMENT_MAX_W,
        maxH: PLACEMENT_MAX_H,
        square: false,
      };
    default: {
      // 記憶の単位を増やしたときに、ここで型エラーとして気づけるようにする。
      const exhaustiveCheck: never = key;
      return exhaustiveCheck;
    }
  }
}

/** 既定サイズの算出に要る、配置時点の画面の状態。 */
export interface PlacementDefaultsContext {
  /** 配置前パネルで選んでいるテキストの文字サイズ（px）。テキストの初期高さに使う。 */
  readonly textFontSize: number;
  /** 配置時点の拡大率。スタンプの初期サイズに使う。 */
  readonly zoom: number;
}

/**
 * 記憶したサイズが無いときの既定サイズ。スタンプは拡大率から、テキストの高さは文字サイズから決める
 * 従来の規則をそのまま使う。
 */
export function defaultPlacementSizeOf(
  key: PlacementSizeKey,
  context: PlacementDefaultsContext
): { readonly w: number; readonly h: number } {
  switch (key) {
    case "sticky":
      return { w: STICKY_DEFAULT_W, h: STICKY_DEFAULT_H };
    case "text":
      return { w: TEXT_DEFAULT_W, h: textInitialHeight(context.textFontSize) };
    case "section":
      return { w: SECTION_DEFAULT_W, h: SECTION_DEFAULT_H };
    case "shape:rectangle":
      return resolveShapeDefaultFrameSize("rectangle");
    case "shape:circle":
      return resolveShapeDefaultFrameSize("circle");
    case "shape:arrow":
      return resolveShapeDefaultFrameSize("arrow");
    case "table":
      return { w: TABLE_DEFAULT_W, h: TABLE_DEFAULT_H };
    case "stamp": {
      const size = resolveStampPlacementSize(context.zoom);
      return { w: size, h: size };
    }
    default: {
      const exhaustiveCheck: never = key;
      return exhaustiveCheck;
    }
  }
}

/**
 * 記憶したサイズ（ステージ px）。
 * テキストだけは高さを持たないことがある。幅だけを手で変えたときの高さは本文に合わせた自動追随の
 * 結果であり、利用者が選んだ高さではないため記憶しない（配置時は文字サイズから決まる初期高さを使う）。
 */
export interface RememberedPlacementSize {
  readonly w: number;
  readonly h?: number | undefined;
}

/** 配置に使うサイズの解決結果。 */
export interface ResolvedPlacementSize {
  readonly w: number;
  readonly h: number;
  /** 記憶したサイズを使ったか。false は既定サイズ。 */
  readonly remembered: boolean;
  /** 高さを記憶値から決めたか。テキストでは高さの自動縮小を止める（manualHeight）判断に使う。 */
  readonly rememberedHeight: boolean;
}

/** 範囲外の値を種類ごとの制約へ丸める。スタンプは幅に高さをそろえて 1:1 を保つ。 */
export function clampPlacementSize(
  key: PlacementSizeKey,
  size: { readonly w: number; readonly h: number }
): { readonly w: number; readonly h: number } {
  const limits = placementSizeLimitsOf(key);
  const w = clampDimension(size.w, limits.minW, limits.maxW);
  const h = limits.square ? w : clampDimension(size.h, limits.minH, limits.maxH);
  return { w, h };
}

/**
 * クリック配置・前回種別の再配置に使うサイズを決める。全経路がこの 1 つの関数を通るため、
 * 経路によってサイズが食い違わない。記憶値が無ければ既定サイズ、あれば制約へ丸めた記憶値を使う
 * （テキストの記憶に高さが無ければ、高さだけ既定の初期高さを使う）。
 */
export function resolvePlacementSize(
  key: PlacementSizeKey,
  remembered: RememberedPlacementSize | undefined,
  context: PlacementDefaultsContext
): ResolvedPlacementSize {
  const fallback = defaultPlacementSizeOf(key, context);
  if (remembered === undefined) {
    return { w: fallback.w, h: fallback.h, remembered: false, rememberedHeight: false };
  }
  const clamped = clampPlacementSize(key, { w: remembered.w, h: remembered.h ?? fallback.h });
  return {
    w: clamped.w,
    h: clamped.h,
    remembered: true,
    rememberedHeight: remembered.h !== undefined,
  };
}

function clampDimension(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}
